/**
 * custom-blend-builder.js
 * Witbran — Custom Blend Builder UI Controller
 *
 * Depends on: WitbranPricing (custom-blend-pricing.js loaded first)
 * Namespace:  WitbranBlend
 * Zero global pollution — all code inside IIFE.
 *
 * Features:
 *   - Grain card toggle with min/max enforcement
 *   - Proportion sliders with 5% steps + stepper buttons
 *   - Auto-balance equaliser
 *   - Real-time pricing engine calls
 *   - Pack size toggle
 *   - Cart API integration with structured line item properties
 *   - Reorder URL param parsing → pre-populate builder
 *   - Step progress bar update
 *   - Accessible (ARIA live regions, keyboard navigation)
 *   - Toast notifications
 */

(function () {
  'use strict';

  // ── Guard ────────────────────────────────────────────────────────────
  var cfg = window.WitbranBlendConfig;
  var pricing = window.WitbranPricing;

  if (!cfg || !pricing) {
    console.error('[WitbranBlend] Missing WitbranBlendConfig or WitbranPricing. Aborting.');
    return;
  }

  // ── State ────────────────────────────────────────────────────────────
  var state = {
    selected: {},     // { grainKey: pct }
    packSize: 5,      // 1 or 5
    isSubmitting: false
  };

  // ── DOM refs ─────────────────────────────────────────────────────────
  var dom = {};

  function init() {
    dom.grainGrid    = document.getElementById('wb-cb-grain-grid');
    dom.sliders      = document.getElementById('wb-cb-sliders');
    dom.totalPct     = document.getElementById('wb-cb-total-pct');
    dom.totalBar     = document.getElementById('wb-cb-total-bar');
    dom.totalBarFill = document.getElementById('wb-cb-total-bar-fill');
    dom.blendBars    = document.getElementById('wb-cb-blend-bars');
    dom.breakdown    = document.getElementById('wb-cb-price-breakdown');
    dom.totalVal     = document.getElementById('wb-cb-total-val');
    dom.atcBtn       = document.getElementById('wb-cb-atc');
    dom.atcHint      = document.getElementById('wb-cb-atc-hint');
    dom.autoBalance  = document.getElementById('wb-cb-auto-balance');
    dom.grainError   = document.getElementById('wb-cb-grain-error');
    dom.propError    = document.getElementById('wb-cb-prop-error');
    dom.toast        = document.getElementById('wb-cb-toast');
    dom.toastMsg     = document.getElementById('wb-cb-toast-msg');
    dom.reorderBanner = document.getElementById('wb-cb-reorder-banner');
    dom.reorderDismiss = document.getElementById('wb-cb-reorder-dismiss');
    dom.steps        = document.querySelectorAll('.wb-cb__step');

    if (!dom.grainGrid) return; // Not on builder page

    // Event listeners
    dom.grainGrid.addEventListener('click', handleGrainCardClick);
    dom.grainGrid.addEventListener('keydown', handleGrainCardKeydown);
    dom.autoBalance.addEventListener('click', handleAutoBalance);
    dom.atcBtn.addEventListener('click', handleAddToCart);

    document.querySelectorAll('.wb-cb__pack-radio').forEach(function (radio) {
      radio.addEventListener('change', handlePackSizeChange);
    });

    if (dom.reorderDismiss) {
      dom.reorderDismiss.addEventListener('click', function () {
        dom.reorderBanner.hidden = true;
      });
    }

    // Check for reorder URL param
    checkReorderParam();

    // Initial render
    render();
  }

  // ── Grain Card Interactions ──────────────────────────────────────────

  function handleGrainCardClick(e) {
    var card = e.target.closest('.wb-cb__grain-card');
    if (!card) return;
    toggleGrain(card);
  }

  function handleGrainCardKeydown(e) {
    if (e.key === 'Enter' || e.key === ' ') {
      var card = e.target.closest('.wb-cb__grain-card');
      if (card) {
        e.preventDefault();
        toggleGrain(card);
      }
    }
  }

  function toggleGrain(card) {
    var key   = card.dataset.grainKey;
    var isOn  = key in state.selected;
    var count = Object.keys(state.selected).length;

    hideError(dom.grainError);

    if (isOn) {
      // Deselect: disallow if it would go below 0 (always allow deselect)
      delete state.selected[key];
    } else {
      // Select: enforce max
      if (count >= cfg.maxGrains) {
        showError(dom.grainError, 'Maximum ' + cfg.maxGrains + ' grains allowed.');
        return;
      }
      // Add with 0% — auto-balance will set proportion
      state.selected[key] = 0;
      // Auto-balance when adding
      state.selected = pricing.autoBalance(Object.keys(state.selected), cfg.stepPct);
    }

    render();
  }

  // ── Proportion Slider Logic ──────────────────────────────────────────

  function buildSliders() {
    var keys  = Object.keys(state.selected);
    var html  = '';

    if (keys.length < cfg.minGrains) {
      dom.sliders.innerHTML = '<p class="wb-cb__sliders-empty">Select at least ' + cfg.minGrains + ' grains above to set proportions.</p>';
      if (dom.autoBalance) dom.autoBalance.hidden = true;
      return;
    }

    if (dom.autoBalance) dom.autoBalance.hidden = false;

    var colors = ['#D4872A', '#6B8F71', '#8B6914', '#2D6B9E', '#8B3A3A', '#5A6B2D'];

    for (var i = 0; i < keys.length; i++) {
      var key   = keys[i];
      var pct   = state.selected[key] || 0;
      var grain = cfg.grains[key];
      var name  = grain ? grain.name : key;
      var color = colors[i % colors.length];
      var fillW = Math.max(0, Math.min(100, pct));

      html += '<div class="wb-cb__slider-row" data-grain-key="' + key + '">' +
        '<div class="wb-cb__slider-dot" style="background:' + color + '" aria-hidden="true"></div>' +
        '<div class="wb-cb__slider-grain-name">' + escHtml(name) + '</div>' +
        '<button type="button" class="wb-cb__stepper-btn" data-action="dec" data-key="' + key + '" aria-label="Decrease ' + escHtml(name) + ' by 5%">−</button>' +
        '<div class="wb-cb__slider-track" role="presentation">' +
          '<div class="wb-cb__slider-fill" style="width:' + fillW + '%;background:' + color + '">' +
            '<div class="wb-cb__slider-thumb" style="background:' + color + '"></div>' +
          '</div>' +
        '</div>' +
        '<button type="button" class="wb-cb__stepper-btn" data-action="inc" data-key="' + key + '" aria-label="Increase ' + escHtml(name) + ' by 5%">+</button>' +
        '<input type="range" class="wb-cb__slider-input sr-only" ' +
          'min="' + cfg.minPct + '" max="100" step="' + cfg.stepPct + '" value="' + pct + '" ' +
          'data-key="' + key + '" ' +
          'aria-label="' + escHtml(name) + ' proportion" ' +
          'aria-valuetext="' + pct + ' percent">' +
        '<div class="wb-cb__slider-pct" aria-hidden="true">' + pct + '%</div>' +
      '</div>';
    }

    dom.sliders.innerHTML = html;

    // Bind stepper button events
    dom.sliders.querySelectorAll('.wb-cb__stepper-btn').forEach(function (btn) {
      btn.addEventListener('click', handleStepperClick);
    });

    // Bind range input events (for keyboard users)
    dom.sliders.querySelectorAll('.wb-cb__slider-input').forEach(function (input) {
      input.addEventListener('input', handleSliderInput);
    });
  }

  function handleStepperClick(e) {
    var btn    = e.currentTarget;
    var action = btn.dataset.action;
    var key    = btn.dataset.key;
    var step   = cfg.stepPct;

    adjustProportion(key, action === 'inc' ? step : -step);
  }

  function handleSliderInput(e) {
    var key = e.currentTarget.dataset.key;
    var val = parseInt(e.currentTarget.value, 10);
    // Snap to step
    val = Math.round(val / cfg.stepPct) * cfg.stepPct;
    adjustProportion(key, val - state.selected[key]);
  }

  function adjustProportion(key, delta) {
    var current  = state.selected[key] || 0;
    var newVal   = current + delta;
    var keys     = Object.keys(state.selected);
    var others   = keys.filter(function (k) { return k !== key; });

    // Clamp: min 5, max (100 - others' minimum)
    var maxAllowed = 100 - (others.length * cfg.minPct);
    newVal = Math.max(cfg.minPct, Math.min(maxAllowed, newVal));

    if (newVal === current) return;

    var diff = newVal - current;
    state.selected[key] = newVal;

    // Redistribute diff across other grains (take/give from largest first)
    others.sort(function (a, b) {
      return state.selected[b] - state.selected[a];
    });

    var remaining = -diff;
    for (var i = 0; i < others.length && remaining !== 0; i++) {
      var otherKey  = others[i];
      var otherCurr = state.selected[otherKey];
      var otherMax  = 100 - cfg.minPct * (others.length - i);
      var canGive   = remaining < 0
        ? Math.max(remaining, cfg.minPct - otherCurr)
        : Math.min(remaining, otherMax - otherCurr);
      state.selected[otherKey] = otherCurr + canGive;
      remaining -= canGive;
    }

    // Final safety: ensure total is exactly 100
    var total = Object.keys(state.selected).reduce(function (s, k) {
      return s + state.selected[k];
    }, 0);

    if (total !== 100) {
      // Adjust last other grain
      var lastKey = others[others.length - 1];
      if (lastKey) {
        state.selected[lastKey] = Math.max(cfg.minPct, state.selected[lastKey] + (100 - total));
      }
    }

    render();
  }

  function handleAutoBalance() {
    state.selected = pricing.autoBalance(Object.keys(state.selected), cfg.stepPct);
    render();
  }

  // ── Pack Size ────────────────────────────────────────────────────────

  function handlePackSizeChange(e) {
    var size = parseInt(e.currentTarget.value, 10);
    state.packSize = size;

    document.querySelectorAll('.wb-cb__pack-card').forEach(function (card) {
      var isSelected = parseInt(card.dataset.size, 10) === size;
      card.classList.toggle('is-selected', isSelected);
    });

    render();
  }

  // ── Cart Integration ─────────────────────────────────────────────────

  function handleAddToCart() {
    if (state.isSubmitting) return;

    var validation = pricing.validateSelection(
      state.selected, cfg.minGrains, cfg.maxGrains, cfg.minPct
    );

    if (!validation.valid) {
      showError(dom.propError, validation.errors.join(' '));
      return;
    }

    var priceResult = computePrice();
    if (priceResult.errors.length > 0) {
      showError(dom.propError, priceResult.errors.join(' '));
      return;
    }

    var variantId = cfg.variants[String(state.packSize)];
    if (!variantId) {
      showError(dom.propError, 'Pack size not available. Please refresh the page.');
      return;
    }

    // Build structured line item properties
    // These appear in the order confirmation, fulfillment team view, and order history
    var blendArray  = pricing.serializeBlend(state.selected, cfg.grains, state.packSize);
    var blendJson   = JSON.stringify(blendArray);

    // Human-readable blend summary (for the cart line item display)
    var blendSummary = blendArray.map(function (g) {
      return g.name + ' ' + g.pct + '%';
    }).join(', ');

    var properties = {
      // Private properties (prefixed _) — hidden from customer in cart display
      '_is_custom_blend': 'true',
      '_blend_json': blendJson,                          // structured data for reorder
      '_calculated_price': priceResult.total.toFixed(2), // for Shopify Function validation

      // Public properties — shown in cart, order emails, packing slips
      'Pack Size': state.packSize + 'kg',
      'Blend': blendSummary,
    };

    // Add individual grain lines (for fulfilment team clarity)
    blendArray.forEach(function (g) {
      properties['↳ ' + g.name] = g.pct + '% (' + g.weightKg + ' kg)';
    });

    var payload = {
      id: variantId,
      quantity: 1,
      properties: properties
    };

    // Set price via selling plan or note — Shopify Functions handles server-side price
    // The variant price is a ₹1 placeholder; the Function sets the real price.
    // We also include it in properties for validation.

    state.isSubmitting = true;
    setAtcLoading(true);

    fetch(cfg.cartUrl + '.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify(payload)
    })
    .then(function (res) {
      if (!res.ok) {
        return res.json().then(function (data) {
          throw new Error(data.description || 'Failed to add to cart');
        });
      }
      return res.json();
    })
    .then(function () {
      showToast('Custom blend added to cart!');
      updateCartCount();
      // Scroll to top smoothly
      window.scrollTo({ top: 0, behavior: 'smooth' });
    })
    .catch(function (err) {
      showError(dom.propError, err.message || 'Could not add to cart. Please try again.');
    })
    .finally(function () {
      state.isSubmitting = false;
      setAtcLoading(false);
    });
  }

  function setAtcLoading(loading) {
    dom.atcBtn.disabled = loading;
    dom.atcBtn.setAttribute('aria-disabled', loading ? 'true' : 'false');
    var spinner = dom.atcBtn.querySelector('.wb-cb__atc-spinner');
    var label   = dom.atcBtn.querySelector('.wb-cb__atc-label');
    if (spinner) spinner.hidden = !loading;
    if (label)   label.textContent = loading ? 'Adding…' : 'Add to Cart';
  }

  function updateCartCount() {
    // Update any cart count badges in the theme header
    fetch('/cart.js')
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var badges = document.querySelectorAll('[data-cart-count], .cart-count, .cart__count, #CartCount');
        badges.forEach(function (el) {
          el.textContent = cart.item_count;
        });
      })
      .catch(function () {});
  }

  // ── Reorder Param ────────────────────────────────────────────────────

  function checkReorderParam() {
    var params   = new URLSearchParams(window.location.search);
    var reorder  = params.get('reorder');
    var size     = params.get('size');

    if (!reorder) return;

    var parsed = pricing.parseReorderParam(decodeURIComponent(reorder));
    if (!parsed) return;

    state.selected = parsed.selection;
    if (size === '1' || size === '5') {
      state.packSize = parseInt(size, 10);
      document.querySelectorAll('.wb-cb__pack-radio').forEach(function (r) {
        r.checked = r.value === size;
      });
    }

    if (dom.reorderBanner) {
      dom.reorderBanner.hidden = false;
    }

    // Update grain card UI
    Object.keys(state.selected).forEach(function (key) {
      var card = document.querySelector('.wb-cb__grain-card[data-grain-key="' + key + '"]');
      if (card) {
        card.classList.add('is-selected');
        card.setAttribute('aria-pressed', 'true');
      }
    });

    render();
  }

  // ── Render ───────────────────────────────────────────────────────────

  function render() {
    var keys  = Object.keys(state.selected);
    var total = keys.reduce(function (s, k) { return s + (state.selected[k] || 0); }, 0);

    // Update grain cards
    document.querySelectorAll('.wb-cb__grain-card').forEach(function (card) {
      var key = card.dataset.grainKey;
      var on  = key in state.selected;
      card.classList.toggle('is-selected', on);
      card.setAttribute('aria-pressed', on ? 'true' : 'false');
    });

    // Update sliders
    buildSliders();

    // Update total bar
    if (dom.totalBar) {
      dom.totalBar.setAttribute('aria-valuenow', total);
      dom.totalBarFill.style.width = Math.min(100, total) + '%';
      dom.totalBarFill.classList.toggle('is-over', total > 100);
      dom.totalBarFill.classList.toggle('is-complete', total === 100);
    }
    if (dom.totalPct) {
      dom.totalPct.textContent = total + '%' + (total === 100 ? ' ✓' : '');
    }

    // Update pack size cards (in case reorder changed it)
    document.querySelectorAll('.wb-cb__pack-card').forEach(function (card) {
      card.classList.toggle('is-selected', parseInt(card.dataset.size, 10) === state.packSize);
    });

    // Compute price
    var priceResult = computePrice();

    // Update blend bars in summary
    renderBlendBars(keys, priceResult);

    // Update breakdown
    renderBreakdown(priceResult);

    // Update total
    if (dom.totalVal) {
      dom.totalVal.textContent = priceResult.errors.length === 0
        ? cfg.currency + formatPrice(priceResult.total)
        : '—';
    }

    // Update pack price labels
    renderPackPrices();

    // Update step indicator
    updateStepBar(keys, total);

    // Update ATC button state
    var isReady = keys.length >= cfg.minGrains &&
                  total === 100 &&
                  priceResult.errors.length === 0;

    dom.atcBtn.disabled = !isReady;
    dom.atcBtn.setAttribute('aria-disabled', isReady ? 'false' : 'true');

    if (dom.atcHint) {
      if (isReady) {
        dom.atcHint.textContent = '';
      } else if (keys.length < cfg.minGrains) {
        dom.atcHint.textContent = 'Select at least ' + cfg.minGrains + ' grains.';
      } else if (total !== 100) {
        dom.atcHint.textContent = 'Proportions must total 100% (currently ' + total + '%).';
      }
    }

    // Hide errors if resolved
    if (keys.length >= cfg.minGrains) hideError(dom.grainError);
    if (total === 100) hideError(dom.propError);
  }

  function computePrice() {
    return pricing.calculatePrice({
      grains:     cfg.grains,
      selection:  state.selected,
      packSizeKg: state.packSize,
      packaging:  cfg.packaging[String(state.packSize)],
      logistics:  cfg.logistics[String(state.packSize)],
      marginPct:  cfg.marginPct
    });
  }

  function renderBlendBars(keys, priceResult) {
    if (!dom.blendBars) return;

    if (keys.length < cfg.minGrains) {
      dom.blendBars.innerHTML = '<p class="wb-cb__blend-empty">Select at least ' + cfg.minGrains + ' grains.</p>';
      return;
    }

    var colors = ['#D4872A', '#6B8F71', '#8B6914', '#2D6B9E', '#8B3A3A', '#5A6B2D'];
    var html   = '';

    for (var i = 0; i < keys.length; i++) {
      var key   = keys[i];
      var pct   = state.selected[key] || 0;
      var name  = cfg.grains[key] ? cfg.grains[key].name : key;
      var color = colors[i % colors.length];

      html += '<div class="wb-cb__blend-bar-row">' +
        '<span class="wb-cb__blend-bar-name">' + escHtml(name) + '</span>' +
        '<div class="wb-cb__blend-bar-track">' +
          '<div class="wb-cb__blend-bar-fill" style="width:' + pct + '%;background:' + color + '"></div>' +
        '</div>' +
        '<span class="wb-cb__blend-bar-pct">' + pct + '%</span>' +
      '</div>';
    }

    dom.blendBars.innerHTML = html;
  }

  function renderBreakdown(priceResult) {
    if (!dom.breakdown) return;

    if (priceResult.errors.length > 0 || priceResult.lineItems.length === 0) {
      dom.breakdown.innerHTML = '<p class="wb-cb__breakdown-empty">Select grains to see pricing.</p>';
      return;
    }

    var cur  = cfg.currency;
    var rows = '';

    priceResult.lineItems.forEach(function (item) {
      rows += makeBreakdownRow(
        item.name + ' (' + item.pct + '% · ' + item.weightKg + 'kg)',
        cur + formatPrice(item.lineTotal)
      );
    });

    rows += makeBreakdownRow('Blend subtotal', cur + formatPrice(priceResult.blendCost));
    rows += makeBreakdownRow('Margin (' + cfg.marginPct + '%)', cur + formatPrice(priceResult.marginAmount));
    rows += makeBreakdownRow('Packaging (' + state.packSize + 'kg)', cur + formatPrice(priceResult.packaging));
    rows += makeBreakdownRow('Logistics (' + state.packSize + 'kg)', cur + formatPrice(priceResult.logistics));

    dom.breakdown.innerHTML = rows;
  }

  function makeBreakdownRow(label, val) {
    return '<div class="wb-cb__price-row">' +
      '<span class="wb-cb__price-label">' + escHtml(label) + '</span>' +
      '<span class="wb-cb__price-val">' + val + '</span>' +
    '</div>';
  }

  function renderPackPrices() {
    // Show price for each pack size option
    ['1', '5'].forEach(function (size) {
      var el = document.querySelector('[data-pack-price="' + size + '"]');
      if (!el) return;

      var result = pricing.calculatePrice({
        grains:     cfg.grains,
        selection:  state.selected,
        packSizeKg: parseInt(size, 10),
        packaging:  cfg.packaging[size],
        logistics:  cfg.logistics[size],
        marginPct:  cfg.marginPct
      });

      el.textContent = result.errors.length === 0
        ? cfg.currency + formatPrice(result.total)
        : '—';
    });

    // Calculate savings (5kg vs 5×1kg)
    var savings = document.getElementById('wb-cb-savings');
    if (savings) {
      var price1 = pricing.calculatePrice({
        grains: cfg.grains, selection: state.selected, packSizeKg: 1,
        packaging: cfg.packaging['1'], logistics: cfg.logistics['1'], marginPct: cfg.marginPct
      });
      var price5 = pricing.calculatePrice({
        grains: cfg.grains, selection: state.selected, packSizeKg: 5,
        packaging: cfg.packaging['5'], logistics: cfg.logistics['5'], marginPct: cfg.marginPct
      });
      if (price1.errors.length === 0 && price5.errors.length === 0) {
        var saved = (price1.total * 5) - price5.total;
        savings.textContent = saved > 0
          ? 'Save ' + cfg.currency + formatPrice(saved) + ' vs 5×1kg'
          : 'Best value';
      }
    }
  }

  function updateStepBar(keys, total) {
    if (!dom.steps || dom.steps.length === 0) return;
    var grainsDone = keys.length >= cfg.minGrains;
    var propsDone  = grainsDone && total === 100;
    var states = ['done', propsDone ? 'done' : (grainsDone ? 'active' : ''), propsDone ? 'active' : '', ''];

    dom.steps.forEach(function (step, i) {
      step.className = 'wb-cb__step';
      if (states[i]) step.classList.add('is-' + states[i]);
      step.setAttribute('aria-current', states[i] === 'active' ? 'step' : 'false');
    });
  }

  // ── Utilities ────────────────────────────────────────────────────────

  function showError(el, msg) {
    if (!el) return;
    if (msg) el.textContent = msg;
    el.hidden = false;
  }

  function hideError(el) {
    if (!el) return;
    el.hidden = true;
  }

  function showToast(msg) {
    if (!dom.toast) return;
    if (dom.toastMsg) dom.toastMsg.textContent = msg;
    dom.toast.hidden = false;
    dom.toast.classList.add('is-visible');
    setTimeout(function () {
      dom.toast.classList.remove('is-visible');
      setTimeout(function () { dom.toast.hidden = true; }, 300);
    }, 3500);
  }

  function formatPrice(n) {
    return Number(n).toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
  }

  function escHtml(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  // ── Boot ─────────────────────────────────────────────────────────────

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Expose for testing
  window.WitbranBlend = {
    getState:   function () { return JSON.parse(JSON.stringify(state)); },
    setState:   function (s) { Object.assign(state, s); render(); },
    render:     render,
    computePrice: computePrice
  };

})();
